/**
 * @mergeTarget
 * @module Src/Parameters/Numeric
 */
import { LisioProfilErrorCodes } from "../profil-error-codes.js";
import { LisioParameter } from "./lisio-parameter.js";
/**
 * Enumerator for all numeric parameters availables in Lisio
 * Please names new parameters as following :
 * ```
 * MY_NAME : "my_name"
 * ```
 * @source
 */
var LisioNumericParameterNames;
(function (LisioNumericParameterNames) {
    LisioNumericParameterNames["FONT_SIZE"] = "font_size";
    LisioNumericParameterNames["LINE_HEIGHT"] = "line_height";
    LisioNumericParameterNames["WORD_SPACING"] = "word_spacing";
    LisioNumericParameterNames["LETTER_SPACING"] = "letter_spacing";
    LisioNumericParameterNames["CONTRAST"] = "contrast";
    LisioNumericParameterNames["ZOOM"] = "zoom";
    LisioNumericParameterNames["BIGGER_CLICK"] = "bigger_click";
    LisioNumericParameterNames["SPEECH_SYNTHESIS_PITCH"] = "speech_synthesis_pitch";
    LisioNumericParameterNames["SPEECH_SYNTHESIS_RATE"] = "speech_synthesis_rate";
    LisioNumericParameterNames["DALTONISM_R"] = "daltonism_r";
    LisioNumericParameterNames["DALTONISM_G"] = "daltonism_g";
    LisioNumericParameterNames["DALTONISM_B"] = "daltonism_b";
    LisioNumericParameterNames["DALTONISM_HUE"] = "daltonism_hue";
    LisioNumericParameterNames["DALTONISM_SATURATION"] = "daltonism_saturation";
    LisioNumericParameterNames["DALTONISM_BRIGHTNESS"] = "daltonism_brightness";
})(LisioNumericParameterNames || (LisioNumericParameterNames = {}));
/**
 * Default value of all parameters
 * @source
 */
const defautValueOfNumericParameters = new Map([
    [LisioNumericParameterNames.FONT_SIZE, 0],
    [LisioNumericParameterNames.LINE_HEIGHT, 1],
    [LisioNumericParameterNames.WORD_SPACING, 0],
    [LisioNumericParameterNames.LETTER_SPACING, 0],
    [LisioNumericParameterNames.CONTRAST, 10],
    [LisioNumericParameterNames.ZOOM, 1],
    [LisioNumericParameterNames.BIGGER_CLICK, 0],
    [LisioNumericParameterNames.SPEECH_SYNTHESIS_PITCH, 1],
    [LisioNumericParameterNames.SPEECH_SYNTHESIS_RATE, 1],
    [LisioNumericParameterNames.DALTONISM_R, 100],
    [LisioNumericParameterNames.DALTONISM_G, 100],
    [LisioNumericParameterNames.DALTONISM_B, 100],
    [LisioNumericParameterNames.DALTONISM_HUE, 0],
    [LisioNumericParameterNames.DALTONISM_SATURATION, 100],
    [LisioNumericParameterNames.DALTONISM_BRIGHTNESS, 100],
]);
/**
 * ## How to create a new (numeric) parameter ? Part 2
 *
 * To create a new numeric parameter is very easy. To begin, choose a revelant name and add it to {@link LisioNumericParameterNames | LisioNumericParameterNames}.\
 * Next you have to add default value for parameter in {@link defautValueOfNumericParameters | defautValueOfNumericParameters}
 *
 * ## How to create a new (numeric) parameter ? Part 2
 *
 * If you want to rename it, choose a new revelant name and modify the old one in {@link LisioNumericParameterNames | LisioNumericParameterNames}.\
 * If you want change his type, see "How to delete" for current parameter and "How to create a new parameter" for new parameter.\
 *
 * ## How to delete a (numeric) parameter ? Part 2
 *
 * Delete his name from {@link LisioNumericParameterNames | LisioNumericParameterNames}.\
 *
 * ## Documentation
 *
 * Class representing a numeric parameter extending LisioParameter.\
 * It aims to centralize all the logic of numeric parameter.\
 * To execute his responsability this class does :
 *  * Saves name and value of numeric parameter
 */
class LisioNumericParameter extends LisioParameter {
    /**
     * Protected attribute to store value of parameter
     * @source
     */
    _value;
    /**
     * Protected attribute to store default value of parameter
     * @source
     */
    _defaultValue;
    /**
     * Getter for attribute {@link _value | _value}
     * @returns Returns _value attribute
     * @source
     */
    get value() {
        return this._value;
    }
    /**
     * Getter for attribute {@link _defaultValue | _defaultValue}
     * @returns Returns _defaultValue attribute
     * @source
     */
    get defaultValue() {
        return this._defaultValue;
    }
    /**
     * Constructor of class {@link LisioNumericParameter | LisioNumericParameter}
     * @param {LisioNumericParameterNames} name - Parameter name
     * @param {number} value - Parameter value
     * @source
     */
    constructor(name, value) {
        super(name);
        this._value = value;
        if (defautValueOfNumericParameters.has(name)) {
            this._defaultValue = defautValueOfNumericParameters.get(name);
        }
        else {
            throw new Error(`Code: ${LisioProfilErrorCodes.WRONG_DEFAULT_VALUE_NUMERIC_PARAMETER}. Parameter "${name}" doesn't exist or default value not defined in map`);
        }
    }
}
export { LisioNumericParameter, LisioNumericParameterNames, defautValueOfNumericParameters, };
